"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreateCreditPurchaseRequest } from "@chotca/sdk";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";

export function useCreateCreditPurchase(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCreditPurchaseRequest) =>
      getSdk().creditPurchases.create(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.creditPurchases.list(orgId) });
      qc.invalidateQueries({ queryKey: qk.orgs.detail(orgId) });
    },
  });
}

export function useCreditPurchases(orgId: string, enabled = true) {
  return useQuery({
    queryKey: ["creditPurchases", "purchases", orgId, "list"],
    queryFn: () => getSdk().creditPurchases.list(orgId, { limit: 20 }),
    enabled: enabled && !!orgId,
  });
}

export function useSyncCreditPurchase(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (purchaseId: string) =>
      getSdk().creditPurchases.sync(orgId, purchaseId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["creditPurchases", "purchases", orgId],
      });
      qc.invalidateQueries({ queryKey: qk.orgs.detail(orgId) });
    },
  });
}

export function useCancelCreditPurchase(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (purchaseId: string) =>
      getSdk().creditPurchases.cancel(orgId, purchaseId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["creditPurchases", "purchases", orgId],
      });
    },
  });
}
